import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Card,
    CardHeader,
    CardBody,
    Button,
    Autocomplete,
    AutocompleteItem,
    Select,
    SelectItem,
    DatePicker,
    NumberInput,
    Input,
    Textarea,
    Chip,
    Spinner,
    Tooltip,
} from "@heroui/react";
import { today, getLocalTimeZone, parseDate } from "@internationalized/date";
import {
    LuArrowLeft,
    LuSave,
    LuRotateCcw,
    LuReceipt,
    LuZap,
    LuInfo,
    LuSearch,
} from "react-icons/lu";
import { useFetchData, usePostData } from "@/hooks";
import { PageHeader, ThemedText, EmptyState } from "@/components/ui";
import { formatCurrency, showToast } from "@/utils";

const SalesPaymentCreate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    // ---- State ----
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [paymentItems, setPaymentItems] = useState([]);

    // ---- Data Fetching ----
    const { data: customers, isLoading: isLoadingCustomers } =
        useFetchData("select/customer");
    const { data: masterCashes, isLoading: isLoadingCashes } =
        useFetchData("select/master-cash");

    const [selectedCashId, setSelectedCashId] = useState(null);
    const [paymentDate, setPaymentDate] = useState(today(getLocalTimeZone()));
    const [description, setDescription] = useState("");
    const [invoiceSearch, setInvoiceSearch] = useState("");

    // Edit mode: fetch existing payment data
    const { data: editData, isLoading: isLoadingEdit } = useFetchData(
        isEdit ? `sales-payment/${id}/edit` : null,
    );

    // Populate form from edit data
    useEffect(() => {
        if (!editData) return;
        const p = editData.payment;
        setSelectedCustomerId(p.relation_table_id);
        setSelectedCashId(p.master_cash_id);
        setPaymentDate(parseDate(p.payment_date));
        setDescription(p.description || "");

        const existingItems = p.items.map((item) => ({
            sales_order_id: item.relation_table_id,
            order_number: item.order_number,
            order_date: item.order_date,
            total_amount: parseFloat(item.so_total_amount),
            total_return: parseFloat(item.so_total_return || 0),
            total_paid: parseFloat(item.so_total_paid || 0),
            total_discount: parseFloat(item.so_total_discount || 0),
            remaining_due: parseFloat(item.so_remaining_due || 0),
            payment_status: item.payment_status || "-",
            amount: parseFloat(item.amount),
            discount: parseFloat(item.discount) || null,
            item_description: item.description || "",
        }));

        const availableItems = (editData.available_orders || []).map((so) => ({
            sales_order_id: so.id,
            order_number: so.order_number,
            order_date: so.order_date,
            total_amount: parseFloat(so.total_amount),
            total_return: parseFloat(so.total_return || 0),
            total_paid: parseFloat(so.total_paid || 0),
            total_discount: parseFloat(so.total_discount || 0),
            remaining_due: parseFloat(so.remaining_due || 0),
            payment_status: so.payment_status?.name || "-",
            amount: null,
            discount: null,
            item_description: "",
        }));

        setPaymentItems([...existingItems, ...availableItems]);
    }, [editData]);

    // Create mode: fetch unpaid orders when customer changes
    const { data: unpaidOrders, isLoading: isLoadingOrders } = useFetchData(
        !isEdit && selectedCustomerId ? "sales-payment/unpaid-orders" : null,
        !isEdit && selectedCustomerId
            ? { customer_id: selectedCustomerId }
            : {},
    );

    // Reset payment items when unpaid orders change (create mode only)
    useEffect(() => {
        if (isEdit) return;
        if (unpaidOrders && unpaidOrders.length > 0) {
            setPaymentItems(
                unpaidOrders.map((so) => ({
                    sales_order_id: so.id,
                    order_number: so.order_number,
                    order_date: so.order_date,
                    total_amount: parseFloat(so.total_amount),
                    total_return: parseFloat(so.total_return || 0),
                    total_paid: parseFloat(so.total_paid || 0),
                    total_discount: parseFloat(so.total_discount || 0),
                    remaining_due: parseFloat(so.remaining_due || 0),
                    payment_status: so.payment_status?.name || "-",
                    amount: null,
                    discount: null,
                    item_description: "",
                })),
            );
        } else {
            setPaymentItems([]);
        }
    }, [unpaidOrders]);

    // ---- Mutation ----
    const { mutate, isLoading: isMutating } = usePostData(
        isEdit ? `sales-payment/${id}/update` : "sales-payment/store",
    );

    // ---- Computed ----
    const totalPayment = useMemo(() => {
        return paymentItems.reduce((sum, item) => {
            return sum + (parseFloat(item.amount) || 0);
        }, 0);
    }, [paymentItems]);

    const totalDiscount = useMemo(() => {
        return paymentItems.reduce((sum, item) => {
            return sum + (parseFloat(item.discount) || 0);
        }, 0);
    }, [paymentItems]);

    const filledItems = useMemo(() => {
        return paymentItems.filter(
            (item) =>
                parseFloat(item.amount) > 0 || parseFloat(item.discount) > 0,
        );
    }, [paymentItems]);

    const filteredPaymentItems = useMemo(() => {
        if (!invoiceSearch.trim()) return paymentItems;
        const q = invoiceSearch.toLowerCase();
        return paymentItems.filter((item) =>
            item.order_number.toLowerCase().includes(q),
        );
    }, [paymentItems, invoiceSearch]);

    // ---- Handlers ----
    const updateItem = (index, field, value) => {
        setPaymentItems((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleFillRemaining = (index) => {
        const item = paymentItems[index];
        const remaining = item.remaining_due - (parseFloat(item.discount) || 0);
        updateItem(index, "amount", remaining > 0 ? remaining : 0);
    };

    const handleResetItem = (index) => {
        setPaymentItems((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                amount: null,
                discount: null,
                item_description: "",
            };
            return updated;
        });
    };

    const handleSubmit = () => {
        if (!selectedCustomerId) {
            showToast("error", "Validasi", "Customer wajib dipilih");
            return;
        }
        if (!selectedCashId) {
            showToast("error", "Validasi", "Jenis kas wajib dipilih");
            return;
        }
        if (!paymentDate) {
            showToast("error", "Validasi", "Tanggal transaksi wajib diisi");
            return;
        }
        if (filledItems.length === 0) {
            showToast(
                "error",
                "Validasi",
                "Minimal 1 invoice harus diisi nominal pembayaran",
            );
            return;
        }

        const payload = {
            customer_id: selectedCustomerId,
            master_cash_id: selectedCashId,
            payment_date: paymentDate.toString(),
            description: description || null,
            items: filledItems.map((item) => ({
                sales_order_id: item.sales_order_id,
                amount: parseFloat(item.amount) || 0,
                discount: parseFloat(item.discount) || 0,
                description: item.item_description || null,
            })),
        };

        mutate(payload, {
            method: isEdit ? "put" : "post",
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    isEdit
                        ? "Pembayaran berhasil diperbarui"
                        : "Pembayaran berhasil disimpan",
                );
                navigate("/sales-payment");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message ||
                        "Terjadi kesalahan saat menyimpan data",
                );
            },
        });
    };

    if (isEdit && isLoadingEdit) {
        return (
            <div className="flex h-[calc(100vh-3rem)] w-full items-center justify-center">
                <Spinner label="Memuat data pembayaran..." color="primary" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={
                        isEdit
                            ? "Edit Pembayaran Penjualan"
                            : "Buat Pembayaran Penjualan"
                    }
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        {
                            label: "Pembayaran Penjualan",
                            href: "/sales-payment",
                        },
                        {
                            label: isEdit
                                ? "Edit Pembayaran"
                                : "Buat Pembayaran",
                        },
                    ]}
                />
            </div>

            {/* Two Column Layout */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
                {/* LEFT: Payment Info (sticky) */}
                <div className="w-full shrink-0 lg:sticky lg:top-4 lg:w-80 xl:w-96">
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 px-6 py-4">
                            <ThemedText className="font-semibold">
                                Informasi Pembayaran
                            </ThemedText>
                        </CardHeader>
                        <CardBody className="flex flex-col gap-4 p-6">
                            {/* Customer */}
                            <Autocomplete
                                label="Customer"
                                placeholder="Pilih customer"
                                isLoading={isLoadingCustomers}
                                selectedKey={
                                    selectedCustomerId
                                        ? String(selectedCustomerId)
                                        : null
                                }
                                onSelectionChange={(key) => {
                                    setSelectedCustomerId(
                                        key ? Number(key) : null,
                                    );
                                }}
                                variant="bordered"
                                isRequired
                                isClearable={false}
                                isDisabled={isEdit}
                            >
                                {(customers || []).map((c) => (
                                    <AutocompleteItem
                                        key={String(c.value)}
                                        textValue={c.title}
                                    >
                                        {c.title}
                                    </AutocompleteItem>
                                ))}
                            </Autocomplete>

                            {/* Payment Date */}
                            <DatePicker
                                label="Tanggal Transaksi"
                                value={paymentDate}
                                onChange={setPaymentDate}
                                variant="bordered"
                                isRequired
                            />

                            {/* Cash Category */}
                            {/* Master Cash */}
                            <Select
                                label="Jenis Kas"
                                placeholder="Pilih jenis kas"
                                isLoading={isLoadingCashes}
                                selectedKeys={
                                    selectedCashId
                                        ? new Set([String(selectedCashId)])
                                        : new Set()
                                }
                                onSelectionChange={(keys) => {
                                    const val = Array.from(keys)[0];
                                    setSelectedCashId(val ? Number(val) : null);
                                }}
                                variant="bordered"
                                isRequired
                            >
                                {(masterCashes || []).map((c) => (
                                    <SelectItem key={String(c.value)}>
                                        {c.title}
                                    </SelectItem>
                                ))}
                            </Select>

                            {/* Description */}
                            <Textarea
                                label="Keterangan"
                                placeholder="Opsional"
                                value={description}
                                onValueChange={setDescription}
                                variant="bordered"
                                minRows={2}
                            />

                            {/* Summary */}
                            <div className="flex flex-col gap-2">
                                {filledItems.length > 0 && (
                                    <div className="border-default-200 flex flex-col gap-2 border-t border-dashed pt-2">
                                        <div className="flex items-center justify-between">
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                Invoice dibayar
                                            </ThemedText>
                                            <ThemedText size="sm">
                                                {filledItems.length}
                                            </ThemedText>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                Subtotal
                                            </ThemedText>
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {formatCurrency(
                                                    totalPayment +
                                                        totalDiscount,
                                                )}
                                            </ThemedText>
                                        </div>
                                    </div>
                                )}
                                {totalDiscount > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between">
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                Diskon
                                            </ThemedText>
                                            <ThemedText
                                                size="sm"
                                                className="text-danger font-medium"
                                            >
                                                -{" "}
                                                {formatCurrency(totalDiscount)}
                                            </ThemedText>
                                        </div>
                                        {filledItems
                                            .filter(
                                                (item) =>
                                                    parseFloat(item.discount) >
                                                    0,
                                            )
                                            .map((item) => (
                                                <div
                                                    key={item.sales_order_id}
                                                    className="flex items-center justify-between pl-2"
                                                >
                                                    <ThemedText
                                                        size="xs"
                                                        variant="caption"
                                                    >
                                                        {item.order_number}
                                                    </ThemedText>
                                                    <ThemedText
                                                        size="xs"
                                                        variant="caption"
                                                    >
                                                        -{" "}
                                                        {formatCurrency(
                                                            parseFloat(
                                                                item.discount,
                                                            ),
                                                        )}
                                                    </ThemedText>
                                                </div>
                                            ))}
                                    </div>
                                )}
                                <div className="border-default-200 mt-2 flex items-center justify-between border-t border-dashed pt-2">
                                    <ThemedText size="sm" variant="secondary">
                                        Total Pembayaran
                                    </ThemedText>
                                    <ThemedText className="text-lg font-bold">
                                        {formatCurrency(totalPayment)}
                                    </ThemedText>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="mt-3 flex gap-2">
                                <Button
                                    variant="flat"
                                    size="sm"
                                    onPress={() => navigate(-1)}
                                    className="flex-1"
                                >
                                    Batal
                                </Button>
                                <Button
                                    color="primary"
                                    size="sm"
                                    isLoading={isMutating}
                                    onPress={handleSubmit}
                                    startContent={!isMutating && <LuSave />}
                                    isDisabled={filledItems.length === 0}
                                    className="flex-1"
                                >
                                    Simpan
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                {/* RIGHT: Invoice List */}
                <div className="min-w-0 flex-1">
                    <Card className="border-default-200 border" shadow="none">
                        <CardHeader className="bg-default-100/70 flex items-center justify-between gap-4 px-6 py-4">
                            <div className="flex flex-col gap-0.5">
                                <ThemedText className="font-semibold">
                                    Daftar Invoice
                                </ThemedText>
                                {paymentItems.length > 0 && (
                                    <ThemedText size="xs" variant="secondary">
                                        {paymentItems.length} invoice
                                    </ThemedText>
                                )}
                            </div>
                            {paymentItems.length > 0 && (
                                <Input
                                    size="sm"
                                    placeholder="Cari nomor transaksi..."
                                    value={invoiceSearch}
                                    onValueChange={setInvoiceSearch}
                                    startContent={
                                        <LuSearch
                                            size={14}
                                            className="text-default-400 shrink-0"
                                        />
                                    }
                                    variant="bordered"
                                    className="max-w-xs"
                                    isClearable
                                />
                            )}
                        </CardHeader>
                        <CardBody className="p-6">
                            {!selectedCustomerId ? (
                                <EmptyState
                                    icon={LuReceipt}
                                    title="Pilih Customer"
                                    description="Pilih customer terlebih dahulu untuk melihat daftar invoice yang belum lunas."
                                />
                            ) : isLoadingOrders ? (
                                <div className="flex h-48 items-center justify-center">
                                    <Spinner label="Memuat invoice..." />
                                </div>
                            ) : paymentItems.length === 0 ? (
                                <EmptyState
                                    icon={LuReceipt}
                                    title="Tidak Ada Invoice"
                                    description="Semua invoice customer ini sudah lunas."
                                />
                            ) : filteredPaymentItems.length === 0 ? (
                                <EmptyState
                                    icon={LuSearch}
                                    title="Invoice Tidak Ditemukan"
                                    description={`Tidak ada invoice dengan nomor "${invoiceSearch}".`}
                                />
                            ) : (
                                <div className="flex flex-col gap-4">
                                    {filteredPaymentItems.map((item, index) => (
                                        <Card
                                            key={item.sales_order_id}
                                            className="border-default-200 border"
                                            shadow="none"
                                        >
                                            <CardBody className="p-4">
                                                {/* Invoice Header */}
                                                <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                                                    <div className="flex flex-col gap-0.5">
                                                        <div className="flex items-center gap-2">
                                                            <ThemedText className="font-semibold">
                                                                {
                                                                    item.order_number
                                                                }
                                                            </ThemedText>
                                                            <Chip
                                                                size="sm"
                                                                variant="flat"
                                                                color={
                                                                    item.payment_status ===
                                                                    "Lunas"
                                                                        ? "success"
                                                                        : "warning"
                                                                }
                                                            >
                                                                {
                                                                    item.payment_status
                                                                }
                                                            </Chip>
                                                        </div>
                                                        <ThemedText
                                                            size="xs"
                                                            variant="secondary"
                                                        >
                                                            {item.order_date}
                                                        </ThemedText>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Tooltip content="Reset nominal pada form ini">
                                                            <Button
                                                                size="sm"
                                                                variant="flat"
                                                                color="danger"
                                                                isIconOnly
                                                                onPress={() =>
                                                                    handleResetItem(
                                                                        index,
                                                                    )
                                                                }
                                                            >
                                                                <LuRotateCcw
                                                                    size={14}
                                                                />
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                </div>

                                                {/* Invoice Summary */}
                                                <div className="bg-default-50 mb-4 grid grid-cols-2 gap-x-6 gap-y-1 rounded-lg p-3 md:grid-cols-5">
                                                    <div>
                                                        <Tooltip
                                                            content="Nilai total tagihan sales order"
                                                            placement="top"
                                                        >
                                                            <div className="flex cursor-default items-center gap-0.5">
                                                                <ThemedText
                                                                    size="xxs"
                                                                    variant="secondary"
                                                                >
                                                                    Total
                                                                </ThemedText>
                                                                <LuInfo
                                                                    size={10}
                                                                    className="text-default-400"
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                        <ThemedText size="sm">
                                                            {formatCurrency(
                                                                item.total_amount,
                                                            )}
                                                        </ThemedText>
                                                    </div>
                                                    <div>
                                                        <Tooltip
                                                            content="Nilai barang retur yang mengurangi tagihan"
                                                            placement="top"
                                                        >
                                                            <div className="flex cursor-default items-center gap-0.5">
                                                                <ThemedText
                                                                    size="xxs"
                                                                    variant="secondary"
                                                                >
                                                                    Retur
                                                                </ThemedText>
                                                                <LuInfo
                                                                    size={10}
                                                                    className="text-default-400"
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                        <ThemedText
                                                            size="sm"
                                                            className={
                                                                item.total_return >
                                                                0
                                                                    ? "text-danger"
                                                                    : ""
                                                            }
                                                        >
                                                            {item.total_return >
                                                            0
                                                                ? `- ${formatCurrency(item.total_return)}`
                                                                : "-"}
                                                        </ThemedText>
                                                    </div>
                                                    <div>
                                                        <Tooltip
                                                            content="Total pembayaran yang sudah diterima sebelumnya"
                                                            placement="top"
                                                        >
                                                            <div className="flex cursor-default items-center gap-0.5">
                                                                <ThemedText
                                                                    size="xxs"
                                                                    variant="secondary"
                                                                >
                                                                    Sudah
                                                                    Dibayar
                                                                </ThemedText>
                                                                <LuInfo
                                                                    size={10}
                                                                    className="text-default-400"
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                        <ThemedText size="sm">
                                                            {item.total_paid > 0
                                                                ? formatCurrency(
                                                                      item.total_paid,
                                                                  )
                                                                : "-"}
                                                        </ThemedText>
                                                    </div>
                                                    <div>
                                                        <Tooltip
                                                            content="Diskon yang sudah diberikan pada pembayaran sebelumnya"
                                                            placement="top"
                                                        >
                                                            <div className="flex cursor-default items-center gap-0.5">
                                                                <ThemedText
                                                                    size="xxs"
                                                                    variant="secondary"
                                                                >
                                                                    Diskon Sblm
                                                                </ThemedText>
                                                                <LuInfo
                                                                    size={10}
                                                                    className="text-default-400"
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                        <ThemedText size="sm">
                                                            {item.total_discount >
                                                            0
                                                                ? formatCurrency(
                                                                      item.total_discount,
                                                                  )
                                                                : "-"}
                                                        </ThemedText>
                                                    </div>
                                                    <div>
                                                        <Tooltip
                                                            content="Sisa tagihan = Total − Retur − Sudah Dibayar − Diskon Sblm"
                                                            placement="top"
                                                        >
                                                            <div className="flex cursor-default items-center gap-0.5">
                                                                <ThemedText
                                                                    size="xxs"
                                                                    variant="secondary"
                                                                >
                                                                    Sisa
                                                                </ThemedText>
                                                                <LuInfo
                                                                    size={10}
                                                                    className="text-default-400"
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                        <ThemedText
                                                            size="sm"
                                                            className={`font-semibold ${item.remaining_due < 0 ? "text-danger" : "text-success"}`}
                                                        >
                                                            {formatCurrency(
                                                                item.remaining_due,
                                                            )}
                                                        </ThemedText>
                                                    </div>
                                                </div>

                                                {/* Payment Input Fields */}
                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                                    <NumberInput
                                                        label="Nominal Bayar"
                                                        placeholder="0"
                                                        value={item.amount}
                                                        onValueChange={(val) =>
                                                            updateItem(
                                                                index,
                                                                "amount",
                                                                val,
                                                            )
                                                        }
                                                        variant="bordered"
                                                        hideStepper
                                                        minValue={0}
                                                        maxValue={Math.max(0, item.remaining_due - (parseFloat(item.discount) || 0))}
                                                        formatOptions={{
                                                            style: "decimal",
                                                            maximumFractionDigits: 0,
                                                        }}
                                                        endContent={
                                                            <Tooltip content="Otomatis bayar sejumlah sisa tagihan">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleFillRemaining(
                                                                            index,
                                                                        )
                                                                    }
                                                                    className="text-default-400 hover:text-primary transition focus:outline-none"
                                                                >
                                                                    <LuZap
                                                                        size={
                                                                            16
                                                                        }
                                                                    />
                                                                </button>
                                                            </Tooltip>
                                                        }
                                                    />
                                                    <NumberInput
                                                        label="Diskon"
                                                        placeholder="0"
                                                        value={item.discount}
                                                        onValueChange={(
                                                            val,
                                                        ) => {
                                                            const newDiscount =
                                                                parseFloat(
                                                                    val,
                                                                ) || 0;
                                                            const newAmount =
                                                                Math.max(
                                                                    0,
                                                                    item.remaining_due -
                                                                        newDiscount,
                                                                );
                                                            setPaymentItems(
                                                                (prev) => {
                                                                    const updated =
                                                                        [
                                                                            ...prev,
                                                                        ];
                                                                    const realIndex =
                                                                        updated.findIndex(
                                                                            (
                                                                                p,
                                                                            ) =>
                                                                                p.sales_order_id ===
                                                                                item.sales_order_id,
                                                                        );
                                                                    if (
                                                                        realIndex !==
                                                                        -1
                                                                    ) {
                                                                        updated[
                                                                            realIndex
                                                                        ] = {
                                                                            ...updated[
                                                                                realIndex
                                                                            ],
                                                                            discount:
                                                                                val,
                                                                            amount: newAmount,
                                                                        };
                                                                    }
                                                                    return updated;
                                                                },
                                                            );
                                                        }}
                                                        variant="bordered"
                                                        hideStepper
                                                        minValue={0}
                                                        formatOptions={{
                                                            style: "decimal",
                                                            maximumFractionDigits: 0,
                                                        }}
                                                    />
                                                    <Input
                                                        label="Keterangan"
                                                        placeholder="Opsional"
                                                        value={
                                                            item.item_description
                                                        }
                                                        onValueChange={(val) =>
                                                            updateItem(
                                                                index,
                                                                "item_description",
                                                                val,
                                                            )
                                                        }
                                                        variant="bordered"
                                                    />
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default SalesPaymentCreate;
